import { useEffect } from 'react';

import { Text, dialogService } from '@thefarmersfront/kitchen-mobile';

import {
  DIALOG_MESSAGES,
  DIALOG_TITLES,
  FAILED_CHECK_IN_AND_OUT_ERROR_CODE,
  INVALID_QR_CODE_ERROR_CODE,
  NOT_FOUND_QR_CODE_ERROR_CODE,
} from '~/constants';
import { useWorkInfoStore } from '~/store';
import { defaultErrorHandler } from '~/utils';

import { TypeCheckInOutParams, useCheckInOut, useCheckOut } from '../api';
import { useScanGongjeongQR } from '../bridge';

/** 공정 QR 코드 스캔 후 체크인/아웃 처리 훅 */
export const useScanCheckInOut = ({
  onSuccessCheckIn,
  onSuccessCheckOut,
  onSuccessChangeGongjeong,
}: {
  onSuccessCheckIn: () => void;
  onSuccessCheckOut: () => void;
  onSuccessChangeGongjeong: () => void;
}) => {
  const recentCheckInOut = useWorkInfoStore((state) => state.workInfo.recentCheckInOut);
  const getIsCheckIn = useWorkInfoStore((state) => state.actions.getIsCheckIn);
  const { openGongjeongQR, parsedScanData, setParsedScanData } = useScanGongjeongQR();

  const requestCheckInOut = useCheckInOut();
  const requestCheckOut = useCheckOut();

  const handleCheckInOut = async ({
    parsedScanData,
    type,
  }: {
    parsedScanData: TypeCheckInOutParams;
    type: 'CHECK_IN' | 'CHANGE_GONGJEONG';
  }) => {
    await requestCheckInOut({
      params: parsedScanData,
      onSuccess: (response) => {
        if (response) {
          if (type === 'CHECK_IN') {
            onSuccessCheckIn();
          }
          if (type === 'CHANGE_GONGJEONG') {
            onSuccessChangeGongjeong();
          }
        }
      },
      onError: (error) => {
        const { code, message } = error;

        if ([NOT_FOUND_QR_CODE_ERROR_CODE, INVALID_QR_CODE_ERROR_CODE].includes(code)) {
          dialogService.addDialog({
            title: DIALOG_TITLES.INVALID_QR_CODE,
            children: <Text color="secondary">{message}</Text>,
            confirmText: '닫기',
          });
          return;
        }
        if (FAILED_CHECK_IN_AND_OUT_ERROR_CODE === code) {
          dialogService.addDialog({
            title: DIALOG_TITLES.FAILED_CHECK_IN,
            children: <Text color="secondary">{message}</Text>,
            confirmText: '닫기',
          });
          return;
        }

        defaultErrorHandler(error);
      },
      onFinally: () => {
        setParsedScanData(null);
      },
    });
  };

  const getIsSameGongjeong = (parsedScanData: TypeCheckInOutParams) => {
    const {
      cluster: scannedCluster,
      center: scannedCenter,
      workPart: scannedWorkPart,
      workProcessCode: scannedWorkProcessCode,
      workProcessDetailName: scannedWorkProcessDetailName,
    } = parsedScanData;

    return (
      recentCheckInOut?.workProcessCode === scannedWorkProcessCode &&
      recentCheckInOut?.workProcessDetailName === scannedWorkProcessDetailName &&
      recentCheckInOut?.cluster.code === scannedCluster &&
      recentCheckInOut?.center.code === scannedCenter &&
      recentCheckInOut?.workPart.code === scannedWorkPart
    );
  };

  useEffect(() => {
    if (!parsedScanData) return;

    /** 체크인 처리 */
    if (!getIsCheckIn()) {
      handleCheckInOut({ parsedScanData, type: 'CHECK_IN' });
      return;
    }

    const isSameGongjeong = getIsSameGongjeong(parsedScanData);

    if (!isSameGongjeong) {
      /** 같은 공정이 아니라면 공정 변경 처리 */
      handleCheckInOut({ parsedScanData, type: 'CHANGE_GONGJEONG' });
      return;
    }

    /** 같은 공정이라면 체크아웃 처리 */
    dialogService.addDialog({
      type: 'confirm',
      title: DIALOG_TITLES.ASK_CHECK_OUT_SAME_GONGJEONG,
      children: <Text color="secondary">{DIALOG_MESSAGES.ASK_CHECK_OUT_SAME_GONGJEONG}</Text>,
      confirmText: '체크아웃',
      onConfirm: () => {
        requestCheckOut({
          onSuccess: (response) => {
            if (response) {
              onSuccessCheckOut();
            }
          },
        });
      },
    });
  }, [parsedScanData]);

  return {
    openGongjeongQR,
  };
};
